<template>
  <b-modal id="salesman-modal" v-model="visible" size="xl" centered hide-header hide-footer
           modal-class="smn-modal" body-class="smn-modal__body">
    <div class="smn">
      <!-- Hero -->
      <div class="smn__hero">
        <div class="smn__hero-icon"><i class="fa-solid fa-user-tie"></i></div>
        <div class="smn__hero-text">
          <h4 class="smn__hero-title">{{ label || $t('salesDashboard.salesAgents') }}</h4>
          <span class="smn__hero-sub">{{ $t('salesDashboard.onHisClients') }}</span>
        </div>
        <button type="button" class="smn__close" @click="visible = false"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="smn__inner">
          <!-- Tabs: the salesman's own FTD clients vs. the retention money others made on them -->
          <div class="smn__tabs">
            <button type="button" class="smn__tab" :class="{ 'is-active': activeTab === 'FTD' }" @click="activeTab = 'FTD'">
              <i class="fa-solid fa-users mr-50"></i>{{ $t('salesDashboard.ftdClientsTab') }}
              <span class="smn__tab-count">{{ ftdTotal }}</span>
            </button>
            <button type="button" class="smn__tab" :class="{ 'is-active': activeTab === 'RETENTION' }" @click="activeTab = 'RETENTION'">
              <i class="fa-solid fa-hand-holding-dollar mr-50"></i>{{ $t('salesDashboard.retentionTab') }}
              <span class="smn__tab-count">{{ total }}</span>
            </button>
          </div>

          <!-- ===== FTD clients tab ===== -->
          <div v-if="activeTab === 'FTD'">
            <div class="smn__total">
              <span class="smn__total-label">{{ $t('salesDashboard.totalDeposited') }}</span>
              <span class="smn__total-value">{{ convertToEURO(ftdDepositsTotal) }}</span>
            </div>
            <div class="smn__table-wrap">
              <b-table small responsive hover :items="ftdClients" :fields="ftdFields" show-empty class="smn-table mb-0"
                       @row-clicked="openClient">
                <template #cell(clientName)="d"><span class="font-weight-bold">{{ d.value || $t('salesDashboard.unknownClient') }}</span></template>
                <template #cell(totalDeposit)="d"><span class="font-weight-bold text-success">{{ convertToCurrency(d.value, d.item.currency) }}</span></template>
                <template #cell(country)="d"><span>{{ d.value || '—' }}</span></template>
                <template #cell(email)="d"><span>{{ d.value || '—' }}</span></template>
                <template #cell(approvedByName)="d"><span>{{ d.value || '—' }}</span></template>
                <template #empty><div class="text-center text-muted py-3">{{ $t('salesDashboard.noData') }}</div></template>
              </b-table>
            </div>
            <div class="smn__footer" v-if="ftdTotal > ftdSize">
              <b-pagination v-model="ftdPage" :total-rows="ftdTotal" :per-page="ftdSize" size="sm" class="mb-0" @change="onFtdPage"/>
              <b-form-select v-model="ftdSize" size="sm" :options="[25, 50, 100]" style="width: 80px" @change="onFtdSize"/>
            </div>
          </div>

          <!-- ===== Retention tab ===== -->
          <div v-else>
            <div class="smn__total">
              <span class="smn__total-label">{{ $t('salesDashboard.totalDeposited') }}</span>
              <span class="smn__total-value text-success">+{{ convertToEURO(retentionIn) }}</span>
              <span class="smn__total-value text-danger" v-if="retentionOut">-{{ convertToEURO(Math.abs(retentionOut)) }}</span>
            </div>
            <!-- Per-agent summary -->
            <div class="smn__section-title">{{ $t('salesDashboard.byRetention') }}</div>
            <div v-if="!byAgent.length" class="text-muted text-center py-2">{{ $t('salesDashboard.noDownstream') }}</div>
            <div v-else class="smn__agents">
              <div v-for="(a, i) in byAgent" :key="a.userId || i" class="smn-agent">
                <span class="smn-agent__avatar">{{ initials(a.name) }}</span>
                <div class="smn-agent__main">
                  <span class="smn-agent__name">{{ a.name }}
                    <span v-if="roleLetter(a)" class="smn-agent__role" :class="'is-' + roleLetter(a).toLowerCase()">{{ roleLetter(a) }}</span>
                  </span>
                  <span class="smn-agent__count">{{ a.count }} {{ $t('salesDashboard.depositsCount') }}</span>
                </div>
                <div class="smn-agent__money">
                  <span class="smn-agent__in">+{{ convertToEURO(a.in) }}</span>
                  <span class="smn-agent__out" v-if="a.out">-{{ convertToEURO(Math.abs(a.out)) }}</span>
                </div>
              </div>
            </div>

            <!-- Transactions -->
            <div class="smn__section-title mt-1">{{ $t('salesDashboard.transactions') }}</div>
            <div class="smn__table-wrap">
              <b-table small responsive hover :items="transactions" :fields="fields" show-empty class="smn-table mb-0"
                       @row-clicked="openClient">
                <template #cell(clientName)="d"><span class="font-weight-bold">{{ d.value || $t('salesDashboard.unknownClient') }}</span></template>
                <template #cell(amount)="d">
                  <span class="font-weight-bold" :class="d.item.type === 'WITHDRAWAL' ? 'text-danger' : 'text-success'">
                    {{ convertToCurrency(d.value, d.item.currency) }}
                  </span>
                </template>
                <template #cell(type)="d">
                  <b-badge pill :variant="d.value === 'WITHDRAWAL' ? 'light-danger' : 'light-success'">{{ d.value }}</b-badge>
                </template>
                <template #cell(flags)="d">
                  <b-badge v-if="d.item.isFtd" pill variant="light-primary" class="mr-25">FTD</b-badge>
                  <b-badge v-if="d.item.isStd" pill variant="light-info">STD</b-badge>
                  <span v-if="!d.item.isFtd && !d.item.isStd" class="text-muted">—</span>
                </template>
                <template #cell(agentName)="d"><span>{{ d.value || '—' }}</span></template>
                <template #cell(approvedByName)="d"><span>{{ d.value || '—' }}</span></template>
                <template #cell(country)="d"><span>{{ d.value || '—' }}</span></template>
                <template #cell(registrationTime)="d"><span class="text-nowrap">{{ d.value | momentDateTime }}</span></template>
                <template #empty><div class="text-center text-muted py-3">{{ $t('salesDashboard.noDownstream') }}</div></template>
              </b-table>
            </div>
            <div class="smn__footer" v-if="total > size">
              <b-pagination v-model="page" :total-rows="total" :per-page="size" size="sm" class="mb-0" @change="onPage"/>
              <b-form-select v-model="size" size="sm" :options="[25, 50, 100]" style="width: 80px" @change="onSize"/>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';

export default {
  name: 'SalesmanModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    loading: false,
    userId: null,
    label: '',
    ctx: {},
    activeTab: 'FTD',
    // FTD-clients tab
    ftdClients: [],
    ftdTotal: 0,
    ftdDepositsTotal: 0,
    ftdPage: 1,
    ftdSize: 25,
    // Retention tab
    byAgent: [],
    transactions: [],
    total: 0,
    page: 1,
    size: 25,
  }),
  computed: {
    fields() {
      return [
        { key: 'clientName', label: this.$t('salesDashboard.client') },
        { key: 'amount', label: this.$t('columns.amount') },
        { key: 'type', label: this.$t('columns.type') },
        { key: 'flags', label: this.$t('columns.ftdStd') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'agentName', label: this.$t('columns.depositorUserId') },
        { key: 'approvedByName', label: this.$t('columns.statusUserId') },
        { key: 'registrationTime', label: this.$t('columns.registrationTime'), class: 'text-nowrap' },
      ];
    },
    ftdFields() {
      return [
        { key: 'clientName', label: this.$t('salesDashboard.client') },
        { key: 'email', label: this.$t('columns.email') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'totalDeposit', label: this.$t('salesDashboard.totalDeposited') },
        { key: 'depositCount', label: this.$t('salesDashboard.depositsCount') },
        { key: 'approvedByName', label: this.$t('columns.statusUserId') },
      ];
    },
    retentionIn() {
      return (this.byAgent || []).reduce((s, a) => s + (a.in || 0), 0);
    },
    retentionOut() {
      return (this.byAgent || []).reduce((s, a) => s + (a.out || 0), 0);
    },
  },
  methods: {
    ...mapActions('dashboard', ['getSalesmanBreakdown']),
    open(p) {
      this.userId = p.userId;
      this.label = p.label || '';
      this.ctx = p.ctx || {};
      this.activeTab = 'FTD';
      this.page = 1;
      this.ftdPage = 1;
      this.byAgent = [];
      this.transactions = [];
      this.ftdClients = [];
      this.total = 0;
      this.ftdTotal = 0;
      this.ftdDepositsTotal = 0;
      this.visible = true;
      this.fetch();
    },
    fetch() {
      this.loading = true;
      this.getSalesmanBreakdown({
        ...this.ctx,
        salesmanId: this.userId,
        pagination: { page: this.page, size: this.size },
        ftdPagination: { page: this.ftdPage, size: this.ftdSize },
      })
        .then(
          res => {
            this.ftdClients = (res && res.ftdClients) || [];
            this.ftdTotal = res && res.ftdClientsTotal != null ? res.ftdClientsTotal : this.ftdClients.length;
            this.ftdDepositsTotal = res && res.ftdDepositsTotal != null ? res.ftdDepositsTotal : 0;
            this.byAgent = (res && res.byAgent) || [];
            this.transactions = (res && res.transactions) || [];
            this.total = res && res.transactionsTotal != null ? res.transactionsTotal : this.transactions.length;
          },
          err => this.showServerErrorNotification('fetchDataError', 'salesDashboard', err)
        )
        .finally(() => { this.loading = false; });
    },
    onPage(p) { this.page = p; this.$nextTick(this.fetch); },
    onSize() { this.page = 1; this.$nextTick(this.fetch); },
    onFtdPage(p) { this.ftdPage = p; this.$nextTick(this.fetch); },
    onFtdSize() { this.ftdPage = 1; this.$nextTick(this.fetch); },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
    },
    roleLetter(a) {
      if (a.role === 'SALE') return 'S';
      if (a.role === 'RETENTION') return 'R';
      return '';
    },
    openClient(item) {
      if (!item || !item.clientId) return;
      const route = this.$router.resolve({ name: 'client', params: { clientId: item.clientId } });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
.smn-modal {
  .modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -20px rgba(20, 24, 50, 0.45); }
  .modal-body.smn-modal__body { padding: 0; }
}
.smn {
  &__hero {
    display: flex; align-items: center; gap: 1rem; padding: 1.2rem 1.5rem; color: #fff;
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }
  &__hero-icon { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 13px; font-size: 1.3rem; background: rgba(255, 255, 255, 0.22); flex-shrink: 0; }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.2rem; }
  &__hero-sub { font-size: 0.82rem; opacity: 0.9; }
  &__close { border: none; background: rgba(255, 255, 255, 0.18); color: #fff; width: 34px; height: 34px; border-radius: 10px; font-size: 1rem; cursor: pointer; flex-shrink: 0; transition: all .2s ease; &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); } }

  &__inner { padding: 1rem 1.25rem 1.25rem; }
  &__tabs { display: flex; gap: 0.4rem; margin-bottom: 0.9rem; }
  &__tab {
    border: none; background: rgba(110, 117, 159, 0.08); color: #6e6b7b;
    padding: 0.5rem 0.9rem; border-radius: 10px; font-weight: 600; font-size: 0.82rem;
    cursor: pointer; display: inline-flex; align-items: center; transition: all .18s ease;
    &:hover { background: rgba(110, 117, 159, 0.16); }
    &.is-active { background: linear-gradient(135deg, #34d399, #10b981); color: #fff; }
  }
  &__tab-count { margin-left: 0.4rem; background: rgba(0, 0, 0, 0.12); border-radius: 8px; padding: 0 7px; font-size: 0.72rem; }
  &__total {
    display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
    padding: 0.6rem 0.85rem; margin-bottom: 0.75rem; border-radius: 12px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.02));
    border: 1px solid rgba(16, 185, 129, 0.18);
  }
  &__total-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #6e6b7b; }
  &__total-value { font-size: 1.05rem; font-weight: 800; color: #10b981; }
  &__section-title { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #a0a4b8; margin-bottom: 0.5rem; }
  &__agents { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.5rem; }
  &__table-wrap { max-height: 46vh; overflow-y: auto; }
  &__footer { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding-top: 0.6rem; }
}
.smn-agent {
  display: flex; align-items: center; gap: 0.7rem;
  padding: 0.5rem 0.6rem; border-radius: 12px; background: rgba(110, 117, 159, 0.05);
  &__avatar { flex-shrink: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 50%; font-size: 0.72rem; font-weight: 700; color: #fff; background: linear-gradient(135deg, #4da3ff, #007BFF); }
  &__main { flex: 1; min-width: 0; }
  &__name { display: block; font-weight: 600; font-size: 0.86rem; color: #1f2a44; }
  &__count { font-size: 0.72rem; color: #b9b9c3; }
  &__role { display: inline-flex; align-items: center; justify-content: center; width: 17px; height: 17px; border-radius: 5px; font-size: 0.6rem; font-weight: 800; color: #fff; margin-left: 0.3rem;
    &.is-s { background: linear-gradient(135deg, #34d399, #10b981); }
    &.is-r { background: linear-gradient(135deg, #4da3ff, #007BFF); } }
  &__money { text-align: right; flex-shrink: 0; }
  &__in { display: block; font-weight: 700; font-size: 0.9rem; color: #10b981; }
  &__out { display: block; font-weight: 600; font-size: 0.78rem; color: #ef4444; }
}
.smn-table { font-size: 0.83rem; tbody tr { cursor: pointer; } thead th { background: #f7f9fc; color: #6e6b7b; text-transform: uppercase; font-size: 0.68rem; } }

body.dark-layout {
  .smn-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .smn-agent { background: rgba(255, 255, 255, 0.04); &__name { color: #d0d2d6; } &__count { color: #7c869c; } }
  .smn-table thead th { background: var(--crm-dark-surface-2, #2e3850); color: #8b93ab; }
}
</style>
